import { useEffect, useState } from "react";
import { Toaster, toast } from "sonner";
import { Check, Clock, Loader2, Lock, LogOut, Phone, Save, Cpu, Ticket, Gamepad2, Plus, Trash2 } from "lucide-react";
import { AdminAuthProvider, useAdminAuth } from "./AdminAuth";
import { api, formatApiErrorDetail } from "./api";
import { SETTINGS_UPDATED_KEY } from "../components/SettingsContext";
import "../App.css";

const EMPTY = {
  systems: { gpu: "", cpu: "", ram: "", monitor: "", internet: "" },
  systems2: { gpu: "", cpu: "", ram: "", monitor: "", internet: "" },
  contact: { lefkosa_phone: "", lefkosa_whatsapp: "", gazimagusa_phone: "", gazimagusa_whatsapp: "", instagram: "", facebook: "", email: "" },
  hours: { lefkosa: "", gazimagusa: "" },
  prices: { lefkosa: null, gazimagusa: null },
  tickets: { lefkosa: { enabled: false, title: "", text: "" }, gazimagusa: { enabled: false, title: "", text: "" } },
  price_items: { lefkosa: [], gazimagusa: [] },
};

const BRANCHES = [{ id: "lefkosa", name: "Lefkoşa" }, { id: "gazimagusa", name: "Gazimağusa" }];

function PriceItemsEditor({ branch, items, onChange }) {
  const update = (i, key, value) => onChange(items.map((item, idx) => (idx === i ? { ...item, [key]: value } : item)));
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, { label: "", price: "", unit: "hour" }]);
  return (
    <fieldset className="admin-branch-fields" data-testid={`admin-price-items-${branch.id}`}>
      <legend className="font-display">{branch.name} Şubesi</legend>
      <div className="admin-rows">
        {items.map((item, i) => (
          <div key={i} className="admin-row" data-testid={`admin-price-item-${branch.id}-${i}`}>
            <input className="admin-input" value={item.label} placeholder="Örn. PS5 / VIP PC / Sınırsız Bilet"
              onChange={(e) => update(i, "label", e.target.value)} data-testid={`admin-price-item-label-${branch.id}-${i}`} />
            <input className="admin-input" type="number" min="0.01" step="0.01" value={item.price ?? ""} placeholder="TL"
              onChange={(e) => update(i, "price", e.target.value)} data-testid={`admin-price-item-price-${branch.id}-${i}`} />
            <select className="admin-input" value={item.unit} onChange={(e) => update(i, "unit", e.target.value)}
              data-testid={`admin-price-item-unit-${branch.id}-${i}`}>
              <option value="hour">TL / saat</option>
              <option value="flat">Tek seferlik</option>
            </select>
            <button type="button" className="admin-row-remove" onClick={() => remove(i)} aria-label="Satırı sil"
              data-testid={`admin-price-item-remove-${branch.id}-${i}`}>
              <Trash2 size={15} aria-hidden="true" />
            </button>
          </div>
        ))}
      </div>
      <button type="button" className="btn btn-outline btn-sm" onClick={add} data-testid={`admin-price-item-add-${branch.id}`}>
        <Plus size={14} aria-hidden="true" /> Ücret satırı ekle
      </button>
    </fieldset>
  );
}

const HINT = "Boş bırakılırsa sitede “Bilgi yakında eklenecek” görünür.";

function Field({ label, value, onChange, placeholder, testid, type = "text", min, step, hint = HINT }) {
  return (
    <div className="admin-field">
      <label htmlFor={testid}>{label}</label>
      <input
        id={testid}
        className="admin-input"
        type={type}
        min={min}
        step={step}
        value={value ?? ""}
        placeholder={placeholder}
        data-testid={testid}
        onChange={(e) => onChange(e.target.value)}
      />
      <span className="admin-hint">{hint}</span>
    </div>
  );
}

function Login() {
  const { setUser } = useAdminAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data.user);
      toast.success(`Hoş geldin, ${data.user.email}`);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="admin-login">
      <form className="admin-login-card" onSubmit={submit} data-testid="admin-login-form">
        <img src="/logo.png" alt="MDR Gaming House logo" data-testid="admin-login-logo" />
        <h1 className="font-display">MDR Admin Panel</h1>
        <p className="admin-sub">Site bilgilerini güncellemek için giriş yap.</p>
        <div className="admin-field">
          <label htmlFor="admin-login-email">E-posta</label>
          <input
            id="admin-login-email"
            className="admin-input"
            type="email"
            required
            value={email}
            placeholder="admin@mdrgaminghouse.com"
            data-testid="admin-login-email"
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="admin-field">
          <label htmlFor="admin-login-password">Şifre</label>
          <input
            id="admin-login-password"
            className="admin-input"
            type="password"
            required
            value={password}
            placeholder="••••••••"
            data-testid="admin-login-password"
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        {error && (
          <p className="admin-error" data-testid="admin-login-error" role="alert">
            <Lock size={13} aria-hidden="true" /> {error}
          </p>
        )}
        <button className="btn btn-primary admin-submit" type="submit" disabled={busy} data-testid="admin-login-submit">
          {busy ? <Loader2 size={16} className="spin" aria-hidden="true" /> : <LogOut size={16} style={{ transform: "rotate(180deg)" }} aria-hidden="true" />}
          {busy ? "Giriş yapılıyor…" : "Giriş Yap"}
        </button>
      </form>
    </div>
  );
}

function Panel() {
  const { setUser } = useAdminAuth();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    api
      .get("/settings")
      .then((res) => setForm({ ...EMPTY, ...res.data }))
      .catch(() => toast.error("Ayarlar yüklenemedi"))
      .finally(() => setLoading(false));
  }, []);

  const set = (section, key) => (value) =>
    setForm((f) => ({ ...f, [section]: { ...f[section], [key]: value } }));
  const setTicket = (branch, key) => (value) =>
    setForm((f) => ({ ...f, tickets: { ...f.tickets, [branch]: { ...f.tickets[branch], [key]: value } } }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const prices = Object.fromEntries(
        Object.entries(form.prices).map(([branch, value]) => [branch, value === "" || value == null ? null : Number(value)])
      );
      const price_items = Object.fromEntries(
        Object.entries(form.price_items).map(([branch, items]) => [
          branch,
          items.filter((item) => item.label.trim()).map((item) => ({
            ...item,
            price: item.price === "" || item.price == null ? null : Number(item.price),
          })),
        ])
      );
      const { data } = await api.put("/admin/settings", { ...form, prices, price_items });
      setForm({ ...EMPTY, ...data });
      try {
        localStorage.setItem(SETTINGS_UPDATED_KEY, String(Date.now()));
      } catch {
        // Returning to the public page also refreshes settings when storage is unavailable.
      }
      setSavedAt(new Date().toLocaleTimeString("tr-TR"));
      toast.success("Kaydedildi — site güncellendi");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      setUser(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-loading" data-testid="admin-loading">
        <Loader2 size={26} className="spin" aria-hidden="true" />
      </div>
    );
  }

  return (
    <>
      <header className="admin-top">
        <div className="admin-top-inner">
          <div className="nav-brand">
            <img src="/logo.png" alt="MDR Gaming House logo" />
            <span className="bt">
              MDR<small>ADMIN PANEL</small>
            </span>
          </div>
          <button className="btn btn-outline btn-sm" onClick={logout} data-testid="admin-logout">
            <LogOut size={14} aria-hidden="true" /> Çıkış Yap
          </button>
        </div>
      </header>

      <main className="admin-main">
        <h1 className="admin-title font-display">Site Bilgileri</h1>
        <p className="admin-sub">
          Kaydettiğin bilgiler sitede anında yayına girer. Boş bıraktığın alanlar “Bilgi yakında eklenecek” olarak görünür.
        </p>

        <form onSubmit={save} data-testid="admin-settings-form">
          <section className="admin-card">
            <h2 className="font-display">
              <Cpu size={17} aria-hidden="true" /> Sistem 1
            </h2>
            <Field label="GPU" value={form.systems.gpu} onChange={set("systems", "gpu")} placeholder="Örn. NVIDIA GeForce RTX…" testid="admin-field-gpu" />
            <Field label="CPU" value={form.systems.cpu} onChange={set("systems", "cpu")} placeholder="Örn. AMD Ryzen…" testid="admin-field-cpu" />
            <Field label="RAM" value={form.systems.ram} onChange={set("systems", "ram")} placeholder="Örn. 32 GB…" testid="admin-field-ram" />
            <Field label="Monitör" value={form.systems.monitor} onChange={set("systems", "monitor")} placeholder="Örn. 27 inç 240Hz…" testid="admin-field-monitor" />
            <Field label="İnternet" value={form.systems.internet} onChange={set("systems", "internet")} placeholder="Örn. 1 Gbps fiber…" testid="admin-field-internet" />
          </section>

          <section className="admin-card">
            <h2 className="font-display">
              <Cpu size={17} aria-hidden="true" /> Sistem 2
            </h2>
            <Field label="GPU" value={form.systems2.gpu} onChange={set("systems2", "gpu")} placeholder="Örn. ASUS Prime RTX 5080" testid="admin-field-gpu-2" />
            <Field label="CPU" value={form.systems2.cpu} onChange={set("systems2", "cpu")} placeholder="Örn. Ryzen 7 9800X3D" testid="admin-field-cpu-2" />
            <Field label="RAM" value={form.systems2.ram} onChange={set("systems2", "ram")} placeholder="Örn. 32 GB DDR5" testid="admin-field-ram-2" />
            <Field label="Monitör" value={form.systems2.monitor} onChange={set("systems2", "monitor")} placeholder="Örn. ASUS ROG Strix 27&quot; 485 Hz" testid="admin-field-monitor-2" />
            <Field label="İnternet" value={form.systems2.internet} onChange={set("systems2", "internet")} placeholder="Örn. 1 Gbps fiber…" testid="admin-field-internet-2" />
          </section>

          <section className="admin-card" data-testid="admin-prices-section">
            <h2 className="font-display">
              <Clock size={17} aria-hidden="true" /> Saatlik PC Ücreti (TL)
            </h2>
            <Field label="Lefkoşa — TL / saat" value={form.prices.lefkosa} onChange={set("prices", "lefkosa")} type="number" min="0.01" step="0.01" placeholder="Örn. 70" testid="admin-field-price-lefkosa" />
            <Field label="Gazimağusa — TL / saat" value={form.prices.gazimagusa} onChange={set("prices", "gazimagusa")} type="number" min="0.01" step="0.01" placeholder="Örn. 75" testid="admin-field-price-gazimagusa" />
          </section>

          <section className="admin-card" data-testid="admin-price-items-section">
            <h2 className="font-display">
              <Gamepad2 size={17} aria-hidden="true" /> Diğer Ücretler (PS5, VIP PC, Bilet)
            </h2>
            <p className="admin-hint">Saatlik PC ücretinin altında listelenir. Satırı silersen siteden de kalkar; fiyatı boş bırakılan satır gösterilmez.</p>
            {BRANCHES.map((branch) => (
              <PriceItemsEditor key={branch.id} branch={branch} items={form.price_items[branch.id] || []}
                onChange={(items) => setForm((f) => ({ ...f, price_items: { ...f.price_items, [branch.id]: items } }))} />
            ))}
          </section>

          <section className="admin-card" data-testid="admin-tickets-section">
            <h2 className="font-display">
              <Ticket size={17} aria-hidden="true" /> Bilet / Paket Sistemi
            </h2>
            <p className="admin-hint">Tek seferlik ödeme ile uzun süre oturma imkânı gibi şubeye özel bilet bilgisi. Fiyat yazmak zorunda değilsin; metin şube kartında gösterilir.</p>
            {[{ id: "lefkosa", name: "Lefkoşa" }, { id: "gazimagusa", name: "Gazimağusa" }].map((branch) => (
              <fieldset key={branch.id} className="admin-branch-fields" data-testid={`admin-ticket-${branch.id}`}>
                <legend className="font-display">{branch.name} Şubesi</legend>
                <label className="admin-toggle" htmlFor={`admin-ticket-enabled-${branch.id}`}>
                  <input id={`admin-ticket-enabled-${branch.id}`} type="checkbox" checked={!!form.tickets[branch.id].enabled}
                    onChange={(e) => setTicket(branch.id, "enabled")(e.target.checked)} data-testid={`admin-ticket-enabled-${branch.id}`} />
                  Bu şubede bilet bilgisini sitede göster
                </label>
                <Field label="Başlık" value={form.tickets[branch.id].title} onChange={setTicket(branch.id, "title")}
                  placeholder="Örn. Bilet Sistemi" hint="Kısa başlık (ör. Bilet Sistemi, Gece Paketi)." testid={`admin-ticket-title-${branch.id}`} />
                <div className="admin-field">
                  <label htmlFor={`admin-ticket-text-${branch.id}`}>Açıklama</label>
                  <textarea id={`admin-ticket-text-${branch.id}`} className="admin-input admin-textarea" value={form.tickets[branch.id].text}
                    placeholder="Örn. Tek seferlik bilet al, uzun saatler boyunca bilgisayarda kal." data-testid={`admin-ticket-text-${branch.id}`}
                    onChange={(e) => setTicket(branch.id, "text")(e.target.value)} />
                  <span className="admin-hint">İstersen süre ve koşulları yazabilirsin; fiyat yazmak zorunlu değil.</span>
                </div>
              </fieldset>
            ))}
          </section>

          <section className="admin-card" data-testid="admin-branch-contacts-section">
            <h2 className="font-display"><Phone size={17} aria-hidden="true" /> Şubelere Özel İletişim</h2>
            {[{ id: "lefkosa", name: "Lefkoşa" }, { id: "gazimagusa", name: "Gazimağusa" }].map((branch) => (
              <fieldset key={branch.id} className="admin-branch-fields" data-testid={`admin-contacts-${branch.id}`}>
                <legend className="font-display">{branch.name} Şubesi</legend>
                {[{ key: "phone", label: "Telefon" }, { key: "whatsapp", label: "WhatsApp" }].map((field) => (
                  <Field key={field.key} label={`${branch.name} ${field.label}`} type="tel"
                    value={form.contact[`${branch.id}_${field.key}`]} onChange={set("contact", `${branch.id}_${field.key}`)}
                    placeholder="Ülke koduyla numara" hint="Ülke koduyla girin (+ veya 00 ile başlayabilir). Boş alan sitede yakında eklenecek olarak görünür."
                    testid={`admin-field-${branch.id}-${field.key}`} />
                ))}
              </fieldset>
            ))}
          </section>

          <section className="admin-card">
            <h2 className="font-display">
              <Phone size={17} aria-hidden="true" /> Sosyal Medya ve E-posta
            </h2>
            <Field label="Instagram" value={form.contact.instagram} onChange={set("contact", "instagram")} placeholder="Kullanıcı adı veya tam link" testid="admin-field-instagram" />
            <Field label="Facebook" value={form.contact.facebook} onChange={set("contact", "facebook")} placeholder="Kullanıcı adı veya tam link" testid="admin-field-facebook" />
            <Field label="E-posta" value={form.contact.email} onChange={set("contact", "email")} placeholder="info@…" type="email" testid="admin-field-email" />
          </section>

          <section className="admin-card">
            <h2 className="font-display">
              <Clock size={17} aria-hidden="true" /> Çalışma Saatleri
            </h2>
            <Field label="Lefkoşa Şubesi" value={form.hours.lefkosa} onChange={set("hours", "lefkosa")} placeholder="Örn. Her gün 10:00 – 02:00" testid="admin-field-hours-lefkosa" />
            <Field label="Gazimağusa Şubesi" value={form.hours.gazimagusa} onChange={set("hours", "gazimagusa")} placeholder="Örn. Her gün 10:00 – 02:00" testid="admin-field-hours-gazimagusa" />
          </section>

          <div className="admin-save-row">
            <button className="btn btn-primary" type="submit" disabled={saving} data-testid="admin-save-button">
              {saving ? <Loader2 size={16} className="spin" aria-hidden="true" /> : <Save size={16} aria-hidden="true" />}
              {saving ? "Kaydediliyor…" : "Kaydet ve Yayınla"}
            </button>
            {savedAt && (
              <span className="admin-saved" data-testid="admin-saved-at">
                <Check size={14} aria-hidden="true" /> Son kayıt: {savedAt}
              </span>
            )}
          </div>
        </form>
      </main>
    </>
  );
}

function AdminInner() {
  const { user } = useAdminAuth();
  if (user === null) {
    return (
      <div className="admin-loading" data-testid="admin-auth-checking">
        <Loader2 size={26} className="spin" aria-hidden="true" />
      </div>
    );
  }
  return user ? <Panel /> : <Login />;
}

export default function AdminApp() {
  return (
    <AdminAuthProvider>
      <div className="admin-root" data-testid="admin-root">
        <AdminInner />
        <Toaster theme="dark" position="top-center" richColors />
      </div>
    </AdminAuthProvider>
  );
}
